/*
 * ef-trifold-3d.js — parametric rotatable 3D preview for the Tri-Fold (folding) fireplace screen.
 * Models the real product: a CENTER mesh panel with two hinged SIDE panels (8" / 10" / 12") that
 * fold back at an angle (wings wrap behind, around the firebox opening) — a free-standing trifold screen. Steel frame recolors to the chosen
 * finish; panels carry a fine grey spark-guard mesh. Resizes live with center W×H and side-panel W.
 *
 * RENDER-ON-DEMAND (same as ef-mantel-3d.js): canvas only re-renders on drag / selection change /
 * resize, so native <select> dropdowns don't flicker and live updates show instantly.
 * Progressive enhancement: if THREE/WebGL is missing, bail (a static image/SVG stays).
 *
 * Public API (ef-pdp-screens.js):
 *   window.EFTrifold3D.setDims(centerWin, heightIn, sidePanelWin)
 *   window.EFTrifold3D.setFinish(hexColorString)   // e.g. '#3a2a1a' bronze
 */
(function () {
  var T = window.THREE;
  var mount = document.getElementById('efTrifold3D');
  if (!mount) return;
  function hasWebGL() {
    try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl'))); }
    catch (e) { return false; }
  }
  if (!T || !hasWebGL()) return;

  var scene = new T.Scene();
  var camera = new T.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(0, 0.6, 6);
  camera.lookAt(0, 0, 0);

  var renderer = new T.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = T.PCFSoftShadowMap;
  if ('outputEncoding' in renderer) renderer.outputEncoding = T.sRGBEncoding;
  mount.appendChild(renderer.domElement);
  renderer.domElement.style.cssText = 'width:100%;height:100%;display:block;touch-action:pan-y;cursor:grab;';

  // ── Lighting ──
  scene.add(new T.HemisphereLight(0xffffff, 0xcfc6b8, 0.7));
  var key = new T.DirectionalLight(0xfff6ea, 1.05);
  key.position.set(3, 6, 6); key.castShadow = true;
  key.shadow.mapSize.width = 1024; key.shadow.mapSize.height = 1024;
  key.shadow.camera.near = 0.5; key.shadow.camera.far = 30;
  key.shadow.camera.left = -8; key.shadow.camera.right = 8; key.shadow.camera.top = 8; key.shadow.camera.bottom = -8;
  key.shadow.bias = -0.0004; scene.add(key);
  var rim = new T.DirectionalLight(0xffffff, 0.5); rim.position.set(-4, 3, -5); scene.add(rim);

  var pivot = new T.Group(); scene.add(pivot);

  // ── Materials ──
  var frameMat = new T.MeshStandardMaterial({ color: 0x222222, roughness: 0.58, metalness: 0.4 }); // matte black default; recolored by finish
  // Real spark-guard MESH (not glass) — dark fine woven grid you can see through. ALPHA-TESTED, not
  // transparency-blended: the holes are cut (alpha<threshold discarded), threads render OPAQUE. This
  // removes transparency sorting entirely → the mesh NEVER disappears at any rotation, and no flicker.
  // Mipmaps OFF so thin threads don't fade below the alpha cutoff at grazing angles/distance. (2026-06-30)
  function meshTexture() {
    var N = 32;
    var c = document.createElement('canvas'); c.width = c.height = N;
    var x = c.getContext('2d'); x.clearRect(0, 0, N, N);                   // fully transparent holes
    x.strokeStyle = 'rgba(20,18,16,1)'; x.lineWidth = 2.2;                 // opaque dark woven wires
    for (var i = 0; i <= N; i += 8) { x.beginPath(); x.moveTo(i, 0); x.lineTo(i, N); x.moveTo(0, i); x.lineTo(N, i); x.stroke(); }
    var tex = new T.CanvasTexture(c); tex.wrapS = tex.wrapT = T.RepeatWrapping;
    tex.generateMipmaps = false; tex.minFilter = T.LinearFilter; tex.magFilter = T.LinearFilter; tex.anisotropy = 8;
    return tex;
  }
  var meshMat = new T.MeshBasicMaterial({ map: meshTexture(), alphaTest: 0.4, transparent: false, side: T.DoubleSide, depthWrite: true, color: 0xdedad4 });

  // ── Build one panel (frame border + mesh) into a group, anchored at left edge x=0 ──
  var FRAME = 0.07, DEPTH = 0.05; // frame bar thickness / panel depth
  function buildPanel(w, h) {
    var g = new T.Group();
    function bar(bw, bh, x, y) { var m = new T.Mesh(new T.BoxGeometry(bw, bh, DEPTH), frameMat); m.castShadow = true; m.position.set(x, y, 0); g.add(m); }
    bar(w, FRAME, w / 2, h - FRAME / 2);          // top
    bar(w, FRAME, w / 2, FRAME / 2);              // bottom
    bar(FRAME, h, FRAME / 2, h / 2);              // left
    bar(FRAME, h, w - FRAME / 2, h / 2);          // right
    var mesh = new T.Mesh(new T.PlaneGeometry(Math.max(w - FRAME * 2, 0.05), Math.max(h - FRAME * 2, 0.05)), meshMat);
    var mt = meshMat.map; if (mt) { mt = mt.clone(); mt.needsUpdate = true; mt.repeat.set(Math.max(w * 5, 2), Math.max(h * 5, 2)); mesh.material = meshMat.clone(); mesh.material.map = mt; }
    mesh.position.set(w / 2, h / 2, DEPTH * 0.12); g.add(mesh); // mesh sits just behind the front frame face
    return g;
  }

  var assembly = new T.Group(); pivot.add(assembly);
  var curCW = 3, curH = 2, curSW = 0.8;

  function build() {
    while (assembly.children.length) { var c = assembly.children.pop(); c.traverse && c.traverse(function (o) { if (o.geometry) o.geometry.dispose(); }); }
    // CENTER panel — centered on x=0
    var center = buildPanel(curCW, curH); center.position.x = -curCW / 2; assembly.add(center);
    var ANG = 32 * Math.PI / 180; // fold angle
    // Wings fold BEHIND the screen (hinged panels angle back around the firebox opening).
    // Camera sits at +z, so BEHIND = -z: left wing needs -ANG, right +ANG. (sign flip 2026-08-08)
    var left = new T.Group(); left.position.set(-curCW / 2, 0, 0); left.rotation.y = -ANG;
    var lp = buildPanel(curSW, curH); lp.position.x = -curSW; left.add(lp); assembly.add(left);
    var right = new T.Group(); right.position.set(curCW / 2, 0, 0); right.rotation.y = ANG;
    var rp = buildPanel(curSW, curH); rp.position.x = 0; right.add(rp); assembly.add(right);

    // Barrel hinges at each panel junction — a vertical stack of knuckles on the BACK face
    // (wings pivot backwards), exactly where the wings fold. (2026-07-01)
    function addHinge(hx) {
      var r = FRAME * 0.5, seg = curH * 0.14; // just two knuckles per junction: one near top, one near bottom
      [curH * 0.2, curH * 0.8].forEach(function (cy) {
        var cyl = new T.Mesh(new T.CylinderGeometry(r, r, seg, 14), frameMat);
        cyl.castShadow = true;
        cyl.position.set(hx, cy, -(DEPTH * 0.5 + r * 0.6));
        assembly.add(cyl);
      });
    }
    addHinge(-curCW / 2); addHinge(curCW / 2);
    // No feet — bare screen + wings, resting on the floor (per photos).
    assembly.position.y = -curH / 2; // center vertically around origin
    ground.position.y = -curH / 2 - 0.02;
  }

  var ground = new T.Mesh(new T.PlaneGeometry(60, 60), new T.ShadowMaterial({ opacity: 0.16 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);

  function frame() {
    var span = curCW + curSW * 2 * Math.cos(38 * Math.PI / 180) + 1.2;
    var vFOV = camera.fov * Math.PI / 180;
    var fitH = (curH + 1.2) / (2 * Math.tan(vFOV / 2));
    var fitW = span / (2 * Math.tan(vFOV / 2) * camera.aspect);
    var dist = Math.max(fitH, fitW, 3.5) * 0.92;
    camera.position.set(0, curH * 0.12, dist);
    camera.lookAt(0, 0, 0);
  }

  // scale inches → scene (center width target ~3.0)
  function rebuild(centerWin, heightIn, sideWin) {
    var TARGET = 3.0, k = TARGET / Math.max(centerWin, 1);
    curCW = TARGET;
    curH = Math.max(heightIn * k, 0.4);
    curSW = Math.max(sideWin * k, 0.2);
    build(); frame(); requestRender();
  }
  rebuild(40, 31, 10);

  // ── Drag rotate ──
  var rotY = 0, rotX = 0.06, dragging = false, lastX = 0, lastY = 0; // load straight-on front; drag to rotate
  function px(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
  function py(e) { return e.touches ? e.touches[0].clientY : e.clientY; }
  function onDown(e) { dragging = true; lastX = px(e); lastY = py(e); renderer.domElement.style.cursor = 'grabbing'; }
  function onMove(e) {
    if (!dragging) return; var x = px(e), y = py(e);
    rotY += (x - lastX) * 0.01; rotX += (y - lastY) * 0.008;
    rotX = Math.max(-0.4, Math.min(0.9, rotX)); lastX = x; lastY = y; requestRender();
    if (e.cancelable && e.touches) e.preventDefault();
  }
  function onUp() { dragging = false; renderer.domElement.style.cursor = 'grab'; }
  renderer.domElement.addEventListener('mousedown', onDown);
  window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  renderer.domElement.addEventListener('touchstart', onDown, { passive: true });
  renderer.domElement.addEventListener('touchmove', onMove, { passive: false });
  renderer.domElement.addEventListener('touchend', onUp);

  function resize() {
    var w = mount.clientWidth || 440, h = mount.clientHeight || 300;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); frame(); requestRender();
  }
  if (window.ResizeObserver) new ResizeObserver(resize).observe(mount); else window.addEventListener('resize', resize);
  resize();

  var needsRender = true;
  function requestRender() { needsRender = true; }
  function loop() {
    requestAnimationFrame(loop);
    if (document.hidden || !needsRender) return;
    pivot.rotation.y = rotY; pivot.rotation.x = rotX;
    renderer.render(scene, camera);
    if (!dragging) needsRender = false;
  }
  loop();
  mount.classList.add('is-active');

  window.EFTrifold3D = {
    setDims: function (cW, h, sW) { rebuild(+cW || 40, +h || 31, +sW || 10); },
    setFinish: function (hex) {
      if (!hex) return; try { frameMat.color.set(hex); requestRender(); } catch (e) {}
    }
  };
})();

/* ── DOM wiring: drive the model from the configurator selections (size / side panels / finish) ── */
(function wireTrifold() {
  var API = window.EFTrifold3D;
  if (!API) return; // WebGL/THREE unavailable → model bailed, nothing to wire
  function frac(s) { // "36 1/8" → 36.125
    if (s == null) return 0; s = ('' + s).trim();
    var m = s.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/); if (m) return +m[1] + (+m[2] / +m[3]);
    m = s.match(/^(\d+)\s*\/\s*(\d+)$/); if (m) return +m[1] / +m[2];
    return parseFloat(s) || 0;
  }
  var st = { cw: 40, h: 31, side: 10, color: null };
  function apply() { API.setDims(st.cw, st.h, st.side); }
  function readMeas() {
    var w = document.getElementById('efCfgWidth'), h = document.getElementById('efCfgHeight');
    if (w && frac(w.value) > 0) st.cw = frac(w.value);
    if (h && frac(h.value) > 0) st.h = frac(h.value);
    apply();
  }
  ['efCfgWidth', 'efCfgHeight'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) { el.addEventListener('input', readMeas); el.addEventListener('change', readMeas); }
  });
  function sampleColor(circle, cb) {
    var bg = circle && circle.style && circle.style.backgroundImage;
    var m = bg && bg.match(/url\(["']?(.*?)["']?\)/);
    if (!m) { cb(null); return; }
    var img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = function () { try { var c = document.createElement('canvas'); c.width = c.height = 6; var x = c.getContext('2d'); x.drawImage(img, 0, 0, 6, 6); var d = x.getImageData(2, 2, 2, 2).data; cb('rgb(' + d[0] + ',' + d[1] + ',' + d[2] + ')'); } catch (e) { cb(null); } };
    img.onerror = function () { cb(null); };
    img.src = m[1];
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var sp = e.target.closest('[data-group="side_panel"] .ef-cfg-card');
    if (sp) { var t = sp.textContent || ''; var mm = t.match(/\b(8|10|12)\b/); if (mm) { st.side = +mm[1]; apply(); } return; }
    var sw = e.target.closest('.ef-cfg-swatch');
    if (sw) { var circ = sw.querySelector('.ef-cfg-swatch__circle'); sampleColor(circ, function (c) { if (c) { st.color = c; API.setFinish(c); } }); }
  }, true);
  setTimeout(readMeas, 500); // initial sync once the form has rendered
})();
